import { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CheckCircle2, Star, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const SCALE = [
    { value: 1, label: "Poor" },
    { value: 2, label: "Unsatisfactory / Average" },
    { value: 3, label: "Satisfactory / Good" },
    { value: 4, label: "Very Satisfactory / Very Good" },
    { value: 5, label: "Over & Above My Expectations" },
];

const COMMON_QUESTIONS = [
    { key: "overall_satisfaction", title: "1. Overall Satisfaction", desc: "On a scale of 1-5, how would you rate your overall experience at Aham Yog Institute?" },
    { key: "instructor_expertise", title: "2. Instructor Expertise", desc: "How satisfied were you with the instructor's knowledge and ability to guide the session effectively?" },
    { key: "facility_ambiance", title: "3. Facility Ambiance", desc: "How would you rate the cleanliness, ambiance, and comfort of our facilities?" },
    { key: "enrollment_process", title: "4. Enrollment Process", desc: "How easy and convenient was the registration / enrollment process for your session?" },
    { key: "program_expectations", title: "5. Program Expectations", desc: "Did the program meet your expectations and personal goals?" },
    { key: "revisit_intentions", title: "6. Revisit Intentions", desc: "How likely are you to return for more classes or workshops at Aham Yog Institute?" },
    { key: "recommendation", title: "7. Recommendation", desc: "Would you recommend our institute to your friends or family?" },
];

const PROGRAM_CATEGORIES = [
    { value: "rope_belt_therapy_yoga", label: "Rope & Belt Therapy Yoga" },
    { value: "certified_courses", label: "Certified Courses" },
    { value: "advance_asana_yoga", label: "Advanced Asana Yoga" },
    { value: "health_workshops", label: "Health Workshops" },
    { value: "corporate_workshops", label: "Corporate Workshops" },
    { value: "regular_yoga_meditation", label: "Regular Yoga & Meditation" },
];

// Conditional block-10 questions per program category.
const BLOCK_QUESTIONS = {
    rope_belt_therapy_yoga: [
        { key: "effectiveness", title: "Effectiveness", desc: "Did the Rope and Belt Therapy help in improving your alignment and flexibility?" },
        { key: "comfort_safety", title: "Comfort and Safety", desc: "Did you feel safe and comfortable using ropes and belts during the session?" },
        { key: "instructor_guidance", title: "Instructor Guidance", desc: "How well did the instructor explain the therapeutic benefits and correct use of props?" },
        { key: "results_noticed", title: "Results Noticed", desc: "Have you noticed any changes in pain relief or posture after attending the sessions?" },
    ],
    advance_asana_yoga: [
        { key: "challenge_level", title: "Challenge Level", desc: "Was the difficulty level of the asanas appropriate for your experience level?" },
        { key: "new_techniques", title: "Learning New Techniques", desc: "Did you learn any new techniques or poses that enhanced your practice?" },
        { key: "instructor_approach", title: "Instructor's Approach", desc: "How well did the instructor manage to provide individual attention during complex poses?" },
        { key: "class_structure", title: "Class Structure", desc: "Was the class well-structured, balancing advanced techniques with safety guidelines?" },
    ],
    certified_courses: [
        { key: "course_content", title: "Course Content", desc: "Did the course cover all the necessary topics (e.g., anatomy, philosophy, teaching methods) in sufficient depth?" },
        { key: "practical_experience", title: "Practical Experience", desc: "Were the practical teaching sessions helpful and engaging?" },
        { key: "certification_value", title: "Certification Value", desc: "Do you feel the certification has adequately prepared you for teaching or advancing in your yoga career?" },
        { key: "support_materials", title: "Support Materials", desc: "How helpful were the study materials and resources provided during the course?" },
    ],
    health_workshops: [
        { key: "relevance_of_topics", title: "Relevance of Topics", desc: "Did the workshop cover topics that were relevant to your personal health or wellness needs?" },
        { key: "practical_application", title: "Practical Application", desc: "Did you gain practical tips or techniques that you can easily incorporate into your daily routine?" },
        { key: "engagement_level", title: "Engagement Level", desc: "How engaging and interactive was the workshop?" },
        { key: "expert_knowledge", title: "Expert Knowledge", desc: "How would you rate the expertise and presentation style of the facilitator?" },
    ],
    corporate_workshops: [
        { key: "workplace_relevance", title: "Workplace Relevance", desc: "Did the workshop address specific workplace challenges, such as stress or team dynamics?" },
        { key: "team_benefits", title: "Team Benefits", desc: "How beneficial was the workshop for your overall team or organization?" },
        { key: "accessibility", title: "Accessibility", desc: "Was the content accessible and suitable for participants of varying yoga experience levels?" },
        { key: "future_interest", title: "Interest in Future Sessions", desc: "Would you be interested in more corporate wellness sessions with Aham Yog Institute?" },
    ],
    regular_yoga_meditation: [
        { key: "class_structure", title: "Class Structure", desc: "Was the sequence and flow of the asanas balanced and effective for your practice level?" },
        { key: "instructor_guidance", title: "Instructor Guidance", desc: "How well did the instructor explain and demonstrate the poses during the session?" },
        { key: "physical_mental_benefits", title: "Physical and Mental Benefits", desc: "Have you experienced any improvements in strength, flexibility, or mental clarity since attending these classes?" },
        { key: "future_interest", title: "Interest in Future Sessions", desc: "Would you be interested in more regular asana yoga class experience at Aham Yog Institute?" },
    ],
};

function RatingPicker({ value, onChange, testId }) {
    return (
        <div className="grid sm:grid-cols-5 gap-2 mt-2" role="radiogroup">
            {SCALE.map((opt) => {
                const selected = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(opt.value)}
                        className={`px-3 py-3 rounded-2xl border text-left transition ${selected ? "bg-brand-50 border-brand-400 text-ink-900 shadow-soft" : "bg-white border-gray-200 hover:border-brand-200"}`}
                        data-testid={`${testId}-${opt.value}`}
                        aria-pressed={selected}
                    >
                        <div className="flex items-center gap-1.5">
                            <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${selected ? "border-brand-500 bg-brand-500" : "border-gray-300"}`}>
                                {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                            </span>
                            <span className="text-xs font-semibold">{opt.value}</span>
                        </div>
                        <div className="text-[11px] text-ink-700 mt-1 leading-tight">{opt.label}</div>
                    </button>
                );
            })}
        </div>
    );
}

function Question({ idx, title, desc, value, onChange, testId }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
            <div className="text-sm font-heading font-semibold text-ink-900">{title}</div>
            <p className="text-sm text-ink-700 mt-1">{desc}</p>
            <RatingPicker value={value} onChange={onChange} testId={testId} />
        </div>
    );
}

export default function FeedbackDialog({ open, onOpenChange, enrollment, onSubmitted }) {
    const [common, setCommon] = useState({});
    const [programCategory, setProgramCategory] = useState("");
    const [programName, setProgramName] = useState("");
    const [blockRatings, setBlockRatings] = useState({});
    const [comments, setComments] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submittedOk, setSubmittedOk] = useState(false);

    // When the dialog opens for a new enrollment, prime from the enrollment record.
    useEffect(() => {
        if (open && enrollment) {
            setCommon({});
            setProgramCategory(enrollment.program_category || "regular_yoga_meditation");
            setProgramName(enrollment.course_title || "");
            setBlockRatings({});
            setComments("");
            setSubmittedOk(false);
        }
    }, [open, enrollment?.id]); // eslint-disable-line

    const blockQs = BLOCK_QUESTIONS[programCategory] || [];

    const allCommonAnswered = useMemo(() => COMMON_QUESTIONS.every((q) => Number.isInteger(common[q.key])), [common]);
    const allBlockAnswered = useMemo(() => blockQs.every((q) => Number.isInteger(blockRatings[q.key])), [blockRatings, blockQs]);
    const canSubmit = !!programCategory && !!programName.trim() && allCommonAnswered && allBlockAnswered;

    const submit = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        try {
            await api.post("/me/feedback", {
                enrollment_id: enrollment.id,
                overall_satisfaction: common.overall_satisfaction,
                instructor_expertise: common.instructor_expertise,
                facility_ambiance: common.facility_ambiance,
                enrollment_process: common.enrollment_process,
                program_expectations: common.program_expectations,
                revisit_intentions: common.revisit_intentions,
                recommendation: common.recommendation,
                program_category: programCategory,
                program_name: programName.trim(),
                block_ratings: blockRatings,
                additional_comments: comments.trim(),
            });
            setSubmittedOk(true);
            onSubmitted?.();
        } catch (err) {
            toast.error("Could not submit feedback", { description: formatApiErrorDetail(err.response?.data?.detail) || err.message });
        } finally {
            setSubmitting(false);
        }
    };

    if (submittedOk) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-lg rounded-3xl text-center" data-testid="feedback-thankyou">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Thank you!</DialogTitle>
                    </DialogHeader>
                    <div className="py-4 flex flex-col items-center">
                        <CheckCircle2 className="h-12 w-12 text-brand-500 mb-3" />
                        <p className="text-ink-900 text-base leading-relaxed">
                            Thank You for sharing your Valuable Feedback with us. Aham Yog Institute will Always thrive to improve and be the Best to serve you Best!
                        </p>
                        <Button onClick={() => onOpenChange(false)} className="mt-6 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6">
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-4xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Program completion feedback</DialogTitle>
                    <DialogDescription>
                        Tell us about your experience with <span className="font-semibold text-ink-900">{enrollment?.course_title}</span>. All ratings are required to mark this enrollment complete.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                    {COMMON_QUESTIONS.map((q, i) => (
                        <Question
                            key={q.key}
                            idx={i + 1}
                            title={q.title}
                            desc={q.desc}
                            value={common[q.key]}
                            onChange={(v) => setCommon((c) => ({ ...c, [q.key]: v }))}
                            testId={`feedback-${q.key}`}
                        />
                    ))}

                    {/* Q8 + Q9 */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
                        <div>
                            <div className="text-sm font-heading font-semibold text-ink-900">8. I attended below Program</div>
                            <Select value={programCategory} onValueChange={setProgramCategory}>
                                <SelectTrigger className="mt-2 rounded-xl" data-testid="feedback-program-category">
                                    <SelectValue placeholder="Select program category" />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROGRAM_CATEGORIES.map((p) => (
                                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-sm font-heading font-semibold text-ink-900">9. Program Name</Label>
                            <input
                                type="text"
                                value={programName}
                                onChange={(e) => setProgramName(e.target.value)}
                                required
                                maxLength={200}
                                className="mt-2 w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-brand-300 outline-none"
                                data-testid="feedback-program-name"
                            />
                            <p className="text-[11px] text-ink-400 mt-1">Auto-filled from your enrolled course — edit if needed.</p>
                        </div>
                    </div>

                    {/* Block-10 conditional questions */}
                    {blockQs.length > 0 && (
                        <div className="space-y-4">
                            <div className="text-[11px] uppercase tracking-wider text-brand-700 font-semibold px-1">10. Program-specific questions</div>
                            {blockQs.map((q, i) => (
                                <Question
                                    key={q.key}
                                    idx={i + 1}
                                    title={`${i + 1}. ${q.title}`}
                                    desc={q.desc}
                                    value={blockRatings[q.key]}
                                    onChange={(v) => setBlockRatings((b) => ({ ...b, [q.key]: v }))}
                                    testId={`feedback-block-${q.key}`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Q11 */}
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
                        <div className="text-sm font-heading font-semibold text-ink-900">11. Additional Comments</div>
                        <p className="text-sm text-ink-700 mt-1">Please provide any other feedback or suggestions for improvement.</p>
                        <Textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={4}
                            maxLength={1400}
                            placeholder="Optional"
                            className="mt-2 rounded-xl"
                            data-testid="feedback-comments"
                        />
                        <div className="text-[11px] text-ink-400 mt-1 text-right">{comments.length}/1400</div>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white pt-4 mt-2 border-t border-gray-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 text-xs">
                        {canSubmit ? (
                            <span className="inline-flex items-center gap-1 text-brand-700 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Ready to submit</span>
                        ) : (
                            <span className="inline-flex items-center gap-1 text-ink-700"><AlertCircle className="h-3.5 w-3.5 text-accent-500" /> Please rate every question to enable submit</span>
                        )}
                    </div>
                    <Button
                        onClick={submit}
                        disabled={!canSubmit || submitting}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="feedback-submit-btn"
                    >
                        <Star className="h-4 w-4 mr-1.5" />
                        {submitting ? "Submitting…" : "Submit feedback"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
